import Basemap from "@arcgis/core/Basemap";
import Point from "@arcgis/core/geometry/Point";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import VectorTileLayer from "@arcgis/core/layers/VectorTileLayer";
import MapView from "@arcgis/core/views/MapView";
import WebMap from "@arcgis/core/WebMap";
import { ArcgisMapCustomEvent } from "@arcgis/map-components";
import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppConfig } from "../../../AppConfig";
import { AppContext, LighthouseContext } from "../../../Context";
import { setIsEditing } from "../../../store/lighthouseSlice";
import { StoreState } from "../../../store/rootReducer";
import { getMapView } from "../../../utils/arcgisUtils";
import { deepCopy } from "../../../utils/helpers";
import { DefaultSectorValues, LighthouseData } from "../../../utils/lighthouseConstants";
import { drawSectors } from "./lighthouseDrawing";
import "./SectorRose.css";
import type View2DConstraints from "@arcgis/core/views/2d/MapViewConstraints";

const SectorRoseMap = () => {
    const dispatch = useDispatch();

    const [map, setMap] = useState<WebMap>();
    const [scale, setScale] = useState<number>(0);
    const [view, setView] = useState<MapView>();
    const [lighthouseData, setLighthouseData] = useState<LighthouseData>();

    const lighthouseContext = useContext(LighthouseContext);
    const appContext = useContext(AppContext);
    const selectedFeature = appContext?.selectedFeature.value;

    const trueNorth = useSelector((state: StoreState) => state.lighthouse.currentLighthouseTrueNorth);
    const sectorOrder = useSelector((state: StoreState) => state.lighthouse.sectorOrder);

    useEffect(() => {
        const mapView = getMapView();
        if (!mapView?.map) return;

        const vectorMap = new Basemap({
            spatialReference: mapView.map.basemap?.spatialReference,
            baseLayers: [
                new VectorTileLayer({
                    url: AppConfig.Basemaps.find((basemap) => basemap.Name === "Default")?.ServiceUrl,
                    opacity: 0,
                }),
            ],
        });

        const sectorRoseLayer = new GraphicsLayer({ id: "SectorRose", title: "Sektorrose", visible: true });

        const embeddedMap = new WebMap({ basemap: vectorMap });
        embeddedMap.add(sectorRoseLayer);
        setMap(embeddedMap);

        adaptLighthouseData(sectorRoseLayer);

        return () => {
            embeddedMap.remove(sectorRoseLayer);
            sectorRoseLayer.destroy();
            embeddedMap.destroy();
            setMap(undefined);
        };
    }, []);

    useEffect(() => {
        const mapViewEmbedded = getMapView("sector-rose-map");
        if (!mapViewEmbedded?.map) return;

        const sectorRoseLayer = mapViewEmbedded.map.findLayerById("SectorRose") as GraphicsLayer;

        adaptLighthouseData(sectorRoseLayer);
    }, [lighthouseContext?.lighthouseData.value, view, sectorOrder]);

    const adaptLighthouseData = (sectorRoseLayer: GraphicsLayer) => {
        sectorRoseLayer.load().then(() => {
            const modifiedLighthouseData = deepCopy(lighthouseContext?.lighthouseData.value) as LighthouseData;
            modifiedLighthouseData.id = "SectorRose";
            modifiedLighthouseData.center = new Point({ x: 0, y: 0, spatialReference: view?.spatialReference });
            modifiedLighthouseData.sectors = modifiedLighthouseData.sectors.map((sector) => {
                const newSector = { ...sector };
                newSector.startLineLength = DefaultSectorValues.defaultInnerRadius;
                newSector.endLineLength = DefaultSectorValues.defaultInnerRadius;
                newSector.innerRadius = DefaultSectorValues.defaultInnerRadius;
                return newSector;
            });

            sectorRoseLayer.removeAll();

            drawSectors({
                graphicsLayer: sectorRoseLayer,
                data: modifiedLighthouseData,
                setIsEditing: (value) => dispatch(setIsEditing(value)),
                sectorsToFill: [],
                opacity: 1,
                drawNumbers: true,
                trueNorthCorrection: trueNorth,
                overrideSectorWidth: true,
                centerFeature: selectedFeature,
                sectorOrder,
            });
            setLighthouseData({ ...modifiedLighthouseData });
        });
    };

    const handleViewReady = (event: ArcgisMapCustomEvent<void>) => {
        if (!lighthouseData) return;

        const eventView = event.target.view;
        setView(eventView);

        // Disable all zooming and panning
        eventView.on("key-down", (event) => {
            const prohibitedKeys = ["+", "-", "Shift", "_", "="];
            const keyPressed = event.key;
            if (prohibitedKeys.indexOf(keyPressed) !== -1) {
                event.stopPropagation();
            }
        });

        eventView.on("mouse-wheel", (event) => {
            event.stopPropagation();
        });

        eventView.on("double-click", (event) => {
            event.stopPropagation();
        });

        eventView.on("double-click", ["Control"], (event) => {
            event.stopPropagation();
        });

        eventView.on("drag", (event) => {
            event.stopPropagation();
        });

        eventView.on("drag", ["Shift"], (event) => {
            event.stopPropagation();
        });

        eventView.on("drag", ["Shift", "Control"], (event) => {
            event.stopPropagation();
        });

        const center = new Point({ x: 0, y: 0, spatialReference: eventView.spatialReference });
        const maxExtentLength = DefaultSectorValues.defaultInnerRadius + AppConfig.Lighthouse.DefaultSectorThickness;

        eventView.goTo(center);
        const extent = eventView.extent.clone();

        const extentPadding = 100;
        extent.centerAt(center);
        extent.xmin = center.x - maxExtentLength - extentPadding;
        extent.xmax = center.x + maxExtentLength + extentPadding;
        extent.ymin = center.y - maxExtentLength - extentPadding;
        extent.ymax = center.y + maxExtentLength + extentPadding;

        eventView.extent = extent;

        eventView.ui.components = [];

        setScale(eventView.scale);
    };

    return (
        map && (
            <arcgis-map
                id="sector-rose-map"
                map={map}
                onarcgisViewReadyChange={handleViewReady}
                constraints={
                    {
                        rotationEnabled: false,
                        minScale: scale,
                        maxScale: scale,
                        snapToZoom: false,
                    } as View2DConstraints
                }
            ></arcgis-map>
        )
    );
};

export default SectorRoseMap;
